import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { config } from "@core/config/config";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { usePostCreation } from "@domain/post/hooks";
import { useModalTools } from "@ui/modals/modalContext";
import { gap, mobile } from "@ui/styles/utils";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Row } from "@ui/components/layout";
import { LoadingBar } from "@ui/components/loadingBar";
import { AssetBrowserModal } from "@ui/components/media/assetBrowser/assetBrowserModal";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { EditPostMedias } from "./editPostMedias";

interface CreatePostInputProps extends React.HTMLAttributes<HTMLDivElement> {
	groupId?: string;
	isMoustache?: string;
}

export const CreatePostInput: React.FC<CreatePostInputProps> = ({ groupId, ...htmlProps }) => {
	const createPost = usePostCreation(groupId);
	const alias = useOrganizationAlias();
	const currentProfile = useCurrentProfile();
	const { createModal } = useModalTools();
	const [videoLoadingProgress, setVideoLoadingProgress] = useState<number | null>(null);
	const isMoustache = config.features.banner;

	return (
		<Container {...htmlProps} $isMoustache={isMoustache}>
			{videoLoadingProgress !== null ? (
				<LoadingBar progress={videoLoadingProgress} />
			) : (
				<Row gap={8} align="stretch">
					<ProfilePicture size="M" picture={alias ? alias.avatar : currentProfile?.avatar} fallbackType={alias?.type} />
					<FakeInput onClick={() => createPost()}>{I18n.t("post.input.placeholder")}</FakeInput>
				</Row>
			)}
			<EditPostMedias
				onImagesUploaded={(images) => createPost(images)}
				onPdfOrVideoUploaded={(pdf) => {
					createPost([pdf]);
					setVideoLoadingProgress(null);
				}}
				onVideoUploadStart={() => setVideoLoadingProgress(0)}
				onVideoProgress={(progress) => setVideoLoadingProgress(progress)}
				onUploadCancel={() => setVideoLoadingProgress(null)}
				onAssetBrowserOpened={() =>
					createModal((close) => (
						<AssetBrowserModal
							multiple
							allowedTypes={["image/*", "application/pdf", "video/*"]}
							close={(assets) => {
								close();
								if (assets && assets.size > 0) {
									createPost(assets.toArray());
								}
							}}
						/>
					))
				}
			/>
		</Container>
	);
};

const Container = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => prop !== "isMoustache",
})<{ $isMoustache?: boolean }>`
	grid-area: input;
	padding: 18px 18px;
	${gap(20)};
	flex-shrink: 0;
	@media ${mobile} {
		${({ $isMoustache }) => ($isMoustache ? "margin-top: 26px" : undefined)}
	}
`;

const FakeInput = styled(RoundedBlock)`
	cursor: pointer;
	flex: 1;
	${({ theme }) => theme.textStyles.secondary};
	color: ${({ theme }) => theme.colors.textTertiary};
	padding: 16px 13px 14px;
`;
